/**
 * Kernarbeitszeiten des Betriebs — die einzige Stelle, an der sie als Daten
 * stehen. In Prosa tauchen dieselben Zeiten in translations/de.ts auf
 * (`generic.openHours` und `call.openHours`); ändern sich die Zeiten, müssen
 * beide Stellen angefasst werden.
 *
 * Der Schnellkontakt darf nur Rückrufzeiten anbieten, zu denen auch wirklich
 * jemand im Haus ist. Deshalb werden die Zeitfenster hier aus den Kernzeiten
 * erzeugt statt als feste Liste gepflegt.
 */

export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri'

export type Slot = { value: string; label: string }

/** Beginn und Ende als Dezimalstunde — 16.5 ist 16:30 Uhr. */
const coreHours: Record<DayKey, { from: number; to: number }> = {
  mon: { from: 7, to: 16.5 },
  tue: { from: 7, to: 16.5 },
  wed: { from: 7, to: 16.5 },
  thu: { from: 7, to: 16.5 },
  fri: { from: 7, to: 13 },
}

export const dayOrder: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri']

export const dayNames: Record<DayKey, string> = {
  mon: 'Montag',
  tue: 'Dienstag',
  wed: 'Mittwoch',
  thu: 'Donnerstag',
  fri: 'Freitag',
}

/** Wert der Option „egal, Hauptsache bald“ im Tag-Dropdown. */
export const ASAP = 'asap'

const formatTime = (value: number) => {
  const hour = Math.floor(value)
  const minute = Math.round((value - hour) * 60)
  return `${hour}:${`${minute}`.padStart(2, '0')}`
}

/**
 * Stündliche Fenster innerhalb der Kernzeit des Tages. Ein Reststück unter
 * einer halben Stunde ergibt kein brauchbares Rückruffenster und fällt weg —
 * am Freitag endet die Liste deshalb um 13:00 Uhr, Montag bis Donnerstag
 * schließt das halbstündige Fenster 16:00 – 16:30 Uhr die Liste ab.
 */
export const slotsForDay = (day: DayKey): Slot[] => {
  const { from, to } = coreHours[day]
  const slots: Slot[] = []

  for (let start = from; start < to; start += 1) {
    const end = Math.min(start + 1, to)
    if (end - start < 0.5) break
    slots.push({ value: `${start}-${end}`, label: `${formatTime(start)} – ${formatTime(end)} Uhr` })
  }

  return slots
}

/**
 * Fenster für „schnellstmöglich“: die Schnittmenge aller Wochentage. Nur so
 * gilt ein gewähltes Fenster an jedem Tag, an dem zurückgerufen werden könnte —
 * sonst stünde am Freitag plötzlich 15:00 Uhr zur Auswahl.
 */
export const slotsForAnyDay = (): Slot[] => {
  const from = Math.max(...dayOrder.map((day) => coreHours[day].from))
  const to = Math.min(...dayOrder.map((day) => coreHours[day].to))
  const slots: Slot[] = []

  for (let start = from; start < to; start += 1) {
    const end = Math.min(start + 1, to)
    if (end - start < 0.5) break
    slots.push({ value: `${start}-${end}`, label: `${formatTime(start)} – ${formatTime(end)} Uhr` })
  }

  return slots
}

export const slotsFor = (day: string): Slot[] =>
  day === ASAP || !day ? slotsForAnyDay() : slotsForDay(day as DayKey)

/** Kernzeiten in einer Zeile — für die Seite und für die Mail an den Betrieb. */
export const coreHoursSummary = () => {
  const groups: { days: DayKey[]; from: number; to: number }[] = []

  dayOrder.forEach((day) => {
    const { from, to } = coreHours[day]
    const last = groups[groups.length - 1]
    if (last && last.from === from && last.to === to) {
      last.days.push(day)
    } else {
      groups.push({ days: [day], from, to })
    }
  })

  return groups
    .map(({ days, from, to }) => {
      const label =
        days.length === 1
          ? dayNames[days[0]]
          : `${dayNames[days[0]]} – ${dayNames[days[days.length - 1]]}`
      return `${label} ${formatTime(from)} – ${formatTime(to)} Uhr`
    })
    .join(' · ')
}
